import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import locale from "date-fns/locale/en-US";
import { useSnackbar } from "notistack";
import DateFnsUtils from "@date-io/date-fns";
import {
  Box,
  IconButton,
  makeStyles,
  TextField,
  Typography
} from "@material-ui/core";
import BackIcon from "@material-ui/icons/ArrowBack";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import {
  setProjects,
  setProjectsQuantity
} from "../../../../store/actions/account";
import { setProjectDetails } from "../../../../store/actions/project";
import { closeSnackbar } from "../../../../store/actions/snackbars";
import API from "../../../../utils/axios";
import { checkIsURL, getDateValue, remCalc } from "../../../../utils/helpers";
import CustomDatePicker from "./CustomDatePicker";
import StyledDrawer from "../../../accountDrawer/StyledDrawer";
import EditDialogActions from "../../../common/EditDialogActions";
import OptimizedTooltip from "../../../common/OptimizedTooltip";
import UnsavedChangesDialog from "../../../dialogs/UnsavedChangesDialog";
import { TAB_IDS } from "../../../data/constants";
import mixpanelWrapper from "../../../../utils/mixpanel";

const useStyles = makeStyles(theme => ({
  header: {
    height: 80,
    borderBottom: "1px solid #eee",
    boxSizing: "border-box"
  },
  title: {
    fontSize: "1.25rem",
    lineHeight: "1.4375rem",
    fontWeight: 500
  },
  backButton: {
    "&.Mui-disabled": {
      opacity: 0.5
    }
  },
  inputField: {
    width: "100%",
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    height: theme.spacing(8.75),
    fontSize: "0.9375rem",
    "& + .MuiTextField-root": {
      marginLeft: theme.spacing(2)
    }
  },
  picker: {
    zIndex: "100000 !important"
  },
  pickerWrapper: {
    alignItems: "center",
    alignContent: "center",
    justifyContent: "center"
  },
  btnSave: {
    textTransform: "none",
    boxShadow: "none !important",
    minWidth: remCalc(51),
    padding: theme.spacing(1.25, 2),
    lineHeight: "1rem",

    "&.Mui-disabled, &.Mui-disabled:hover": {
      backgroundColor: "#1329FE",
      color: "#fff",
      opacity: "38%"
    }
  },
  btnCancel: {
    color: "#808080",
    minWidth: remCalc(63),
    marginRight: theme.spacing(1),
    padding: theme.spacing(1.25, 2),
    lineHeight: "1rem"
  },
  btnGroup: {
    padding: theme.spacing(1.25, 5, 1.25, 0),
    display: "flex",
    justifyContent: "flex-end",
    marginTop: theme.spacing(5)
  },
  weekend: {
    "& button": {
      color: "#B6B6B6"
    }
  }
}));

const dateFns = new DateFnsUtils();

const ProjectsEditDialog = ({
  open,
  isApp,
  isEditingDuplicate,
  currentProject,
  projectsRef,
  projects,
  duplicateId,
  setHasAnyProject,
  totalQuantity,
  setCurrentAction,
  setProjectsQuantity,
  currentTabId,
  setCurrentTabId,
  setProjects,
  saveDisabled,
  setSaveDisabled,
  setDuplicateSaved,
  setProjectDetails,
  currentAction,
  closeSnackbar,
  setReloadCurrentPage
}) => {
  const classes = useStyles();
  const history = useHistory();
  const nameInputRef = useRef();
  const [disabled, setDisabled] = useState(!isEditingDuplicate);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [selectedDate, setSelectedDate] = useState();
  const [nameError, setNameError] = useState("");

  const { enqueueSnackbar } = useSnackbar();

  const activeProject = sessionStorage.currentProject;

  const setSaveState = (theName, theSelectedDate) => {
    if (
      theName.trim().length === 0 ||
      !theSelectedDate ||
      (theSelectedDate &&
        dateFns.isBeforeDay(
          dateFns.date(theSelectedDate),
          dateFns.date(new Date().getTime())
        ))
    ) {
      setSaveDisabled(true);
    } else {
      setSaveDisabled(false);
    }
  };

  const handleChange = e => {
    const isValidProjectName = !checkIsURL(e.target.value);

    if (!isValidProjectName) {
      enqueueSnackbar(
        "The project name can not be a link, please write valid name for the project"
      );
      return;
    }

    setName(e.target.value);
    // setSaveDisabled(false);
    setSaveState(e.target.value, selectedDate);
    if (name.trim() && selectedDate) {
      setDisabled(false);
    }
  };

  const handleSave = () => {
    if (!name.trim()) {
      setNameError("Please fill in this field");
      return;
    }

    setLoading(true);
    if (name.length > 255) {
      enqueueSnackbar("Project name is too long.");
      setLoading(false);
      return;
    }

    switch (currentAction) {
      case "duplicate":
        handleDuplicate();
        break;
      case "edit":
        handleUpdate();
        break;
      default:
        handleAdd();
    }
  };

  const handleDateChange = val => {
    setSelectedDate(val);
    //setSaveDisabled(false);
    setSaveState(name, val);
  };

  const [changesDialog, setChangesDialog] = useState({
    visible: false,
    action: () => setCurrentAction("overview")
  });

  const handleUpdate = (isDuplicate, data) => {
    const id = data ? data.id : currentProject.id;
    const projectDate = Number.isInteger(selectedDate)
      ? selectedDate
      : selectedDate.split("/").reverse().join("-");

    const date = dateFns.format(new Date(projectDate), "dd/MM/yyyy");

    API.patch(`/v1/projects/${id}`, {
      name,
      date
    })
      .then(res => {
        /*
        const duplicated = data ? [{ ...currentProject, ...data }] : [];
        const newData = [...duplicated, ...projects].map(item =>
          item.id === id ? { ...item, name, project_date: projectDate } : item
        );

        if (currentTabId === TAB_IDS.UPCOMING) {
          setProjectsQuantity(totalQuantity + 1);

          setProjects(newData);
        }*/

        setProjects(
          projects.map(project =>
            project.id === id
              ? {
                  ...project,
                  name,
                  project_date: Number(new Date(projectDate))
                }
              : project
          )
        );

        if (!isDuplicate) {
          enqueueSnackbar(`Project "${name}" edited.`);
        } else if (currentTabId === TAB_IDS.UPCOMING) {
          setReloadCurrentPage(true);
        }

        if (id === activeProject) {
          setProjectDetails(name, projectDate);
        }

        duplicateId && setDuplicateSaved(true);
      })
      .catch(err => {
        console.log(err);
        enqueueSnackbar(`Something went wrong, couldn't update the project.`);
      })
      .finally(() => {
        setLoading(false);
        handleClose();
      });
  };

  const handleDuplicate = () => {
    const snackbar = enqueueSnackbar(
      `Creating copy of "${currentProject.name}"...`
    );

    API.post(`/v1/projects/${currentProject.id}/clone`)
      .then(res => {
        isEditingDuplicate.current = res.data.id;

        enqueueSnackbar(`Project "${name}" created.`);
        closeSnackbar(snackbar);

        handleUpdate(true, res.data);
      })
      .catch(err => {
        console.log(err);
        enqueueSnackbar(
          `Something went wrong, couldn't duplicate the project.`
        );
      })
      .finally(() => {
        setLoading(false);
        handleClose();
      });
  };

  const handleAdd = () => {
    API.post("/v1/projects", {
      name,
      date: dateFns.format(new Date(getDateValue(selectedDate)), "dd/MM/yyyy"),
      enable_vehicles: true
    })
      .then(({ data: newProjectData }) => {
        const { id: newProjectId, project_date: newProjectDate } =
          newProjectData;
        mixpanelWrapper.track("Project Create", {
          "Project Date": dateFns.format(newProjectDate, "dd/MM/yyyy"),
          "Project ID": newProjectId
        });
        enqueueSnackbar(`Project "${name}" created.`);
        if (projects.length === 0) {
          setHasAnyProject(true);
        }

        if (!isApp || projects.length) {
          if (currentTabId === TAB_IDS.UPCOMING) {
            setProjects([{ ...newProjectData, id: newProjectId }, ...projects]);
            setProjectsQuantity(totalQuantity + 1);
          }
        }
        history.push(`${newProjectId}`);
      })
      .catch(err => {
        console.log(err);
        enqueueSnackbar(`Something went wrong, couldn't create a project.`);
      })
      .finally(() => {
        setLoading(false);
        handleClose();
      });
  };

  // Close edit panel
  const handleClose = () => {
    setSaveDisabled(true);
    setName("");
    setSelectedDate(null);
    setCurrentAction("overview");
  };

  const warnClose = saveDisabled
    ? handleClose
    : () =>
        setChangesDialog({
          visible: true,
          action: handleClose
        });

  useEffect(() => {
    if (currentProject) {
      let value;
      if (!Number.isInteger(currentProject.project_date)) {
        value = currentProject.project_date.split("/").reverse().join("-");
      } else {
        value = currentProject.project_date;
      }

      const name =
        currentAction === "duplicate"
          ? `Copy of ${currentProject.name}`
          : currentProject.name;
      const date = Math.max(new Date(), new Date(value));

      setName(name);
      setSelectedDate(date);
      if (currentAction === "duplicate") setSaveDisabled(false);
    } else {
      setName("");
      setSelectedDate(new Date());
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProject, open]);

  useEffect(() => {
    if (currentAction !== "duplicate") {
      if (nameInputRef.current) {
        setTimeout(
          () => nameInputRef.current && nameInputRef.current.focus(),
          300
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProject, open]);

  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils} locale={locale}>
      <StyledDrawer
        isApp
        open={open}
        loading={loading}
        handleClose={() => {
          warnClose();
        }}
      >
        <div
          id="project_edit_form"
          style={{ height: "100%", maxHeight: "100%", overflow: "auto" }}
        >
          {/* Header */}
          <Box
            px={3}
            className={classes.header}
            display="flex"
            alignItems="center"
            justifyContent="flex-start"
          >
            <OptimizedTooltip placement="bottom" title="Close">
              <Box ml={-1} mr={2}>
                <IconButton
                  onClick={warnClose}
                  data-cy="driver-closeEdit"
                  className={classes.backButton}
                >
                  <BackIcon />
                </IconButton>
              </Box>
            </OptimizedTooltip>
            <Box alignSelf="center">
              <Typography variant="h5" className={classes.title}>
                {!currentProject
                  ? "New project"
                  : currentAction === "duplicate"
                  ? "Duplicate project"
                  : "Edit project"}
              </Typography>
            </Box>
          </Box>
          <Box pt={3} pl={3} pr={5} mb={1.5}>
            <TextField
              type="text"
              label="Project name"
              name="name"
              value={name || ""}
              error={!!nameError}
              id="edit-name-table"
              inputProps={{
                "data-cy": "projects-nameInput"
              }}
              helperText={nameError}
              className={classes.inputField}
              onChange={handleChange}
              inputRef={nameInputRef}
              variant="outlined"
            />

            <CustomDatePicker
              value={selectedDate}
              classes={classes}
              name={name}
              currentAction={currentAction}
              setDisabled={setDisabled}
              currentProject={currentProject}
              onChange={handleDateChange}
              duplicate={isEditingDuplicate}
            />
          </Box>
          <EditDialogActions
            section="projects"
            handleClose={warnClose}
            handleClick={handleSave}
            disabled={
              (currentAction !== "duplicate" && disabled) || saveDisabled
            }
            actionButtonId="project_form_action_button"
            confirmText={currentAction === "add" ? "Create project" : "Save"}
          />
        </div>
      </StyledDrawer>
      <UnsavedChangesDialog
        open={changesDialog.visible}
        handleClose={() => {
          setChangesDialog({ visible: false, action: () => {} });
        }}
        action={() => {
          changesDialog.action();
          setSaveDisabled(true);
        }}
      />
    </MuiPickersUtilsProvider>
  );
};

const mapStateToProps = state => ({
  projects: state.account.projects,
  totalQuantity: state.account.totalQuantity
});

export default connect(mapStateToProps, {
  setProjects,
  setProjectsQuantity,
  setProjectDetails,
  closeSnackbar
})(ProjectsEditDialog);
